import { useEffect } from "react";

import type { Vehicle } from "@busbud/int-schemas/udm";

import { useAppContext } from "@app/helpers/hooks";
import { getValidSeatsForVehicles } from "@app/helpers/udm/vehicle/get-valid-seats-for-vehicles";
import { useNavigation } from "@app/modules/results/hooks/use-navigation";
import { useResultsSelector } from "@app/modules/results/hooks/use-results-selector";
import { getTripDetailsFetchStatus } from "@app/modules/results/selectors/state-accessors";
import { TripDetailsFetchStatusKey } from "@app/modules/results/state/trip-details-fetch-status";

import {
  addSeat,
  setSeatSelectionFirstError,
  SSF_INVALID_SEATS
} from "../store";

export const useInitializeSeatSelectionStore = (
  trip_id: string,
  vehicle_layouts: Record<string, Vehicle["layout"]>
) => {
  const { translator } = useAppContext();
  const { goBack, selected_seats: selected_seats_qs } = useNavigation();
  const trip_details_fetch_status = useResultsSelector(
    getTripDetailsFetchStatus
  );

  const is_loading_trip_details =
    trip_details_fetch_status[trip_id] === TripDetailsFetchStatusKey.Loading;

  useEffect(() => {
    if (!vehicle_layouts) {
      return;
    }

    const vehicle_keys = Object.keys(vehicle_layouts);

    if (!Array.isArray(selected_seats_qs) || vehicle_keys.length === 0) {
      return;
    }

    const all_segments_have_layout = Object.keys(vehicle_layouts).every(
      segment_id => {
        return !!vehicle_layouts[segment_id];
      }
    );

    if (
      is_loading_trip_details ||
      (!is_loading_trip_details && !all_segments_have_layout)
    ) {
      return;
    }

    const all_valid_seats = getValidSeatsForVehicles(
      selected_seats_qs,
      vehicle_layouts
    );

    if (all_valid_seats.size > 0) {
      all_valid_seats.forEach((seats, segment_id) => {
        for (const seat of seats) {
          addSeat(seat, segment_id);
        }
      });
    } else {
      setSeatSelectionFirstError({
        type: SSF_INVALID_SEATS
      });
      goBack(translator.t("!error.CartDepartureExpired.text"));
    }
  }, [
    vehicle_layouts,
    is_loading_trip_details,
    selected_seats_qs,
    goBack,
    translator
  ]);
};
